import { computed, makeObservable } from 'mobx'
import moment from 'moment'
import { RRule, rrulestr } from 'rrule'

import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import EnpsFormPolicy from 'modules/core/store/models/policies/EnpsFormPolicy'

import questionConfigs from 'modules/surveys/store/collections/questionConfigs'
import questionGroupConfigs from 'modules/surveys/store/collections/questionGroupConfigs'
import questionGroups from 'modules/surveys/store/collections/questionGroups'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import type QuestionGroupConfig from 'modules/surveys/store/models/QuestionGroupConfig'

const Status = {
  draft: 'draft',
  active: 'active',
  finished: 'finished',
}

interface Types {
  [key: string]: string | undefined
}

export const ParticipantTypes: Types = {
  promoters: 'promoters',
  detractors: 'detractors',
  passives: 'passives',
}

interface IForm {
  [key: string]: any
}

class Form extends ModelWithPolicy<IForm> {
  static Status = Status

  constructor(attr: IForm = {}) {
    super(attr)
    makeObservable(this, {
      isDraft: computed,
      isActive: computed,
      isFinished: computed,
      isAnonymous: computed,
      questionGroups: computed,
      questionConfigs: computed,
      activeQuestionConfigs: computed,
      questionGroupConfigs: computed,
      periodicity: computed,
      hasSchedule: computed,
    })
  }

  get indexes() {
    return ['access_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/enps/forms'
  }

  get policy() {
    return new EnpsFormPolicy(this)
  }

  get isDraft(): boolean {
    return this.attributes.state === Status.draft
  }

  get isActive(): boolean {
    return this.attributes.state === Status.active
  }

  get isFinished(): boolean {
    return this.attributes.state === Status.finished
  }

  get isScheduled(): boolean {
    return (
      this.hasSchedule &&
      new Date(this.attributes.start_on) > new Date(moment().format('YYYY-MM-DD'))
    )
  }

  get isAnonymous(): boolean {
    return this.attributes.anonymous
  }

  get questionGroups(): QuestionGroup[] {
    return questionGroups.filter({
      surveys_form_id: this.attributes.id,
    })
  }

  get questionConfigs(): QuestionConfig[] {
    const questionGroupConfigIds = this.questionGroupConfigs.map((qgc) => qgc.attributes.id)

    return questionConfigs.models.filter((qc) => {
      if (!qc.has('id')) return false

      return questionGroupConfigIds.includes(qc.attributes.surveys_question_group_config_id)
    })
  }

  get activeQuestionConfigs(): QuestionConfig[] {
    return this.questionConfigs.filter((qc) => !qc.isArchived)
  }

  get questionGroupConfigs(): QuestionGroupConfig[] {
    return questionGroupConfigs.filter({
      surveys_form_id: this.attributes.id,
    })
  }

  get periodicity(): string {
    if (this.attributes.schedule) {
      const periodicity = rrulestr(this.attributes.schedule, {
        cache: true,
      }).options

      switch (periodicity.freq) {
        case RRule.YEARLY:
          return 'yearly'
        case RRule.MONTHLY:
          switch (periodicity.interval) {
            case 1:
              return 'monthly'
            case 2:
              return 'bi_monthly'
            case 3:
              return 'quarterly'
            case 4:
              return 'quadrimester'
            case 6:
              return 'bi_annual'
          }
      }
    }

    return 'quarterly'
  }

  get hasSchedule(): boolean {
    return true // we don't rely on backend cycles (see AnalyticResult.ts) thus this check is redundant
  }
}

export default Form
